<template>
  <v-container>
    <loading-overlay :value="processingSubscription" />
    <div class="product-bar" v-if="!hideProductBar">
      <v-icon>
        $engagelogo
      </v-icon>
    </div>
    <div class="back-nav" v-if="!hideBackButton">
      <v-btn text :to="backNavigationTo">
        <v-icon left>mdi-chevron-left</v-icon>
        back
      </v-btn>
    </div>
    <v-row>
      <v-col cols="12" md="7" lg="8">
        <component :is="cardWithPaymentComponent">
          <v-card-title>
            <h1>{{ hasExistingPayment ? "Confirm" : "Add" }} Payment Method</h1>
          </v-card-title>
          <v-card-subtitle class="mt-0">
            <h3 v-if="type === 'VP_ENGAGE_CVC'">
              One of your clients requires you to have a VendorProof
              subscription, which has an annual charge of
              {{ this.price | formatPrice }}.
              {{ hasExistingPayment ? "Confirm" : "Add" }} your payment method
              below.
            </h3>
            <h3 v-else-if="type === 'VP_EMPLOYEE_MONITORING'">
              Your new VendorProof Employee Monitoring subscription requires an
              initial charge of {{ this.price | formatPrice }}.
              {{ hasExistingPayment ? "Confirm" : "Add" }} your payment method
              below.
            </h3>
          </v-card-subtitle>

          <v-card-text>
            <payment-options
              @has-existing-payment="hasExistingPayment = true"
              @has-payment="hasPayment = $event"
              :location="location"
            ></payment-options>
          </v-card-text>
        </component>
      </v-col>
      <v-col cols="12" md="5" lg="4" align-self="center">
        <v-card flat color="transparent" class="ma-0 ma-sm-6">
          <v-card-title class="my-4">
            <h4>Order Summary</h4>
          </v-card-title>
          <v-card-text>
            <div class="pricing">
              <span v-if="type === 'VP_EMPLOYEE_MONITORING'"
                >Monthly Price</span
              >
              <span v-else-if="type === 'VP_ENGAGE_CVC'">VendorProof</span>
              <span class="ml-2">{{ this.price | formatPrice }}</span>
            </div>
            <v-divider class="my-2 price-separator"></v-divider>
            <div class="pricing">
              <span>Total</span>
              <span>{{ this.price | formatPrice }}</span>
            </div>
            <div class="description-text my-6">
              You will be billed
              <span v-if="type === 'VP_ENGAGE_CVC'"
                >annually on {{ new Date() | formatDate("MM/dd") }}</span
              >
              <span v-else-if="type === 'VP_EMPLOYEE_MONITORING'"
                >each month on {{ new Date() | formatDate("do") }} based on the
                number of employees being monitored at the time of
                billing.</span
              >
            </div>
          </v-card-text>
          <v-card-actions v-if="!hideConfirmPayment">
            <v-container>
              <v-btn
                x-large
                class="mb-4 primary--text cta"
                depressed
                color="#FCBE32"
                :block="$vuetify.breakpoint.xsOnly"
                :disabled="!hasPayment || !price"
                @click="save"
              >
                Confirm Order
              </v-btn>
            </v-container>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PaymentOptions from "@/components/PaymentOptions";
import { formatDate, formatPrice } from "@/services/format";
import PtSheet from "@/components/PtSheet.vue";
import { get, post } from "@/services/request";
import { vpSubscriptionsAPI, vpSubscriptionTypesAPI } from "@/services/api";
import LoadingOverlay from "@/components/LoadingOverlay";
import hasNotifications from "@/mixins/has-notifications";
import store from "@/store";

export default {
  name: "SubscriptionPayment",
  components: {
    PaymentOptions,
    PtSheet,
    LoadingOverlay
  },
  mixins: [hasNotifications],
  props: {
    type: {
      type: String,
      required: true,
      validator(value) {
        return ["VP_EMPLOYEE_MONITORING", "VP_ENGAGE_CVC"].includes(value);
      }
    },
    location: {
      type: String,
      default: "Employee Monitoring Subscription Payment"
    },
    backNavigationTo: {
      type: Object,
      default: () => ({
        /* nowhere */
      })
    }
  },
  data: () => ({
    price: null,
    hasPayment: false,
    processingSubscription: false,
    hasExistingPayment: false,
    hideProductBar: false,
    hideBackButton: false,
    hideConfirmPayment: false,
    paymentOptionsWithHighlight: true,
    // type: [Boolean, String, Object],
    // Default (of true) redirects according to type. If false, do not redirect.
    // If String or object, treated as a string path or location descriptor object, and overrides the default type-based redirect
    redirectOnPaymentSuccess: true
  }),
  computed: {
    cardWithPaymentComponent() {
      return this.paymentOptionsWithHighlight ? "pt-sheet" : "div";
    },
    computedRedirectOnPaymentSuccess() {
      if (typeof this.redirectOnPaymentSuccess === "boolean") {
        if (this.redirectOnPaymentSuccess) {
          if (this.type === "VP_EMPLOYEE_MONITORING") {
            return "/employees";
          }
        }
      } else {
        return this.redirectOnPaymentSuccess;
      }

      return undefined;
    },
    fromSignup() {
      return this.location === "Vendor Signup Process Payment";
    }
  },
  watch: {
    hasPayment(val) {
      this.$emit("form-valid", val);
    }
  },
  created() {
    this.loadData();

    if (this.fromSignup) {
      this.hideProductBar = true;
      this.hideBackButton = true;
      this.hideConfirmPayment = true;
      this.paymentOptionsWithHighlight = false;
      this.redirectOnPaymentSuccess = false;
    }
  },
  methods: {
    loadData() {
      get(vpSubscriptionTypesAPI).then(response => {
        if (response && response.data) {
          const subscriptionType = response.data.find(
            ({ subscriptionType }) => subscriptionType === this.type
          );
          if (subscriptionType) this.price = subscriptionType.price;
        }
      });
    },
    save() {
      if (!this.type) {
        // this is technically impossible since the button won't be enabled until we have a price which requires a type to resolve
        console.error("Subscription Type is required to proceed.");
        return Promise.reject();
      }

      this.processingSubscription = true;
      return post(vpSubscriptionsAPI, {
        amount: this.price,
        subscriptionType: this.type
      })
        .then(() => {
          if (!this.fromSignup) {
            this.logEvent("Clicked subscribe button", {
              Result: "Payment successful"
            });
          }

          if (this.type === "VP_EMPLOYEE_MONITORING") {
            store.dispatch("user/loadCurrentUser");
          }
          if (this.computedRedirectOnPaymentSuccess !== undefined) {
            this.$router.push(this.computedRedirectOnPaymentSuccess);
          }
          return Promise.resolve();
        })
        .catch(error => {
          this.showMessage({
            text:
              "Error processing payment, please try again or contact us for support",
            mode: "error"
          });
          console.error("Error processing subscription payment", error);

          if (!this.fromSignup) {
            this.logEvent("Clicked subscribe button", {
              Result: "Payment failed"
            });
          }
          return Promise.reject();
        })
        .then(() => {
          this.processingSubscription = false;
        });
    },
    validate() {
      this.$emit("form-valid", this.hasPayment);
      this.$emit(
        "form-error-message",
        this.hasPayment ? "" : "Please select or enter a payment method"
      );
    }
  },
  filters: {
    formatPrice,
    formatDate
  }
};
</script>

<style lang="sass" scoped>
.pricing
  font-size: 20px
  line-height: 25px
  color: $grey-text-color
  display: flex
  justify-content: space-between

.v-divider.price-separator
  border-color: $lightish-grey !important

.v-btn.v-size--x-large
  font-size: 22px
  line-height: 28px
  height: 64px
  padding: 0 50px

h1
  font-size: 40px
  line-height: 50px
  color: $grey-text-color

h3
  font-size: 24px
  font-weight: normal
  line-height: 31px
  color: $grey-text-color

h4
  font-size: 20px
  line-height: 25px
  color: $grey-text-color
  font-weight: normal !important

.product-bar
  height: 76px
  display: flex
  align-items: center

.v-btn
  font-size: 1rem

.v-btn.v-btn--text
  font-weight: normal
  color: $subtitle
</style>
